import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, retry, timeout } from 'rxjs/operators';
import { of, Observable } from 'rxjs';

import { environment } from '@environments/environment';
import Resources from '@constants/resourses';

import { selectedAddressModel } from '../models/selectedAddress.interface';
import { EvService } from './ev.service';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  storage: any = {};
  constructor(
    private http: HttpClient,
    private injector: Injector
  ) { }

  private get evService(): EvService {
    return this.injector.get(EvService);
  }

  /**
   * Get the session ID
   * @param isNew boolean - whether to generate a new session ID or return the existing one 
   * @returns string - the session ID, which is a base64-encoded JSON string containing the expiry, key, email, and name (if available)
   */
  getSessionId(isNew: boolean = false): string {
    if (this.getItemFromSession(Resources.SessionStorageKeys.SessionId) && !isNew) {
      return this.getItemFromSession(Resources.SessionStorageKeys.SessionId);
    } else {
      const email = this.getData(Resources.SessionStorageKeys.Email);
      const name = this.getData(Resources.SessionStorageKeys.PersonalDetails)?.name;

      const STID = btoa(JSON.stringify({
        expiry: new Date(Date.now() + 3600 * 1000).toISOString(),
        key: "cognito-auth",
        ...(email ? { email } : {}),
        ...(name ? { name } : {})
      }));

      this.setItemIntoSession(Resources.SessionStorageKeys.SessionId, STID);
      return STID;
    }
  }

  /**
   * @description Check user is logged in or not
   * @returns boolean
   */
  isLoggedIn(): boolean {
    return !!this.getItemFromLocal(Resources.SessionStorageKeys.IsLoggedIn);
  }

  /**
   * @param key string
   * @param value any
   * @description Store date into client session
   */
  setItemIntoSession(key: string, value: any): void {
    if (!!key && !!value) {
      sessionStorage.setItem(key, (typeof value == 'string') ? value : JSON.stringify(value));
      return;
    }
  }

  /**
   * 
   * @param key string
   * @description Get data from client session
   */
  getItemFromSession(key: string): any {
    if (!!key) {
      try {
        return JSON.parse(sessionStorage.getItem(key));
      } catch (e) {
        return sessionStorage.getItem(key);
      }
    }
  }

  /**
   * 
   * @param key string
   * @description Remove data from respective key
   */
  removeItemFromSession(key: string): void {
    // Remove from client session
    if (!!key && !!sessionStorage.getItem(key)) {
      sessionStorage.removeItem(key);
    }
  }

  /**
   * @param key string
   * @param value any
   * @description Store date into client local storage
   */
  setItemIntoLocal(key: string, value: any): void {
    if (!!key && !!value) {
      // Store into client local storage
      localStorage.setItem(key, (typeof value == 'string') ? value : JSON.stringify(value));
      return;
    }
  }

  /**
   * 
   * @param key string
   * @description Get data from client local storage
   */
  getItemFromLocal(key: string): any {
    const item = localStorage.getItem(key);
    if (!!item) {
      // Get from client local storage
      try {
        return JSON.parse(item);
      } catch (e) {
        return item;
      }
    }
  }

  /**
   * 
   * @param key string
   * @description Remove data from respective key
   */
  removeItemFromLocal(key: string): void {
    // Remove from client local storage
    if (!!localStorage.getItem(key)) {
      localStorage.removeItem(key);
    }
  }

  /**
   * @description Clear client session storage
   */
  clearSessionStorage(): void {
    sessionStorage.clear();
  }

  /**
   * @description Clear client local storage
   */
  clearLocalStorage(): void {
    localStorage.clear();
  }

  /**
   * @description This method is used to restore session data from the server using correlationId. It fetches the session data and stores it in the session storage.
   * @param correlationId string
   * @returns Observable<any>
   * @throws Error if correlationId is not provided or if the session data cannot be fetched
   * @example this.sessionService.restoreSessionData('12345').subscribe()
   */
  restoreSessionData(correlationId: string, email?: string): Observable<any> {
    if (!correlationId) {
      return of(null);
    }

    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    return this.http.post<any>(`${environment?.api_url}/v1/users/sessionData`, {
      correlationId: correlationId,
      ...(email ? { email } : {})
    }, httpOptions).pipe(
      timeout(30000),
      retry(1),
      map((data: any) => {
        if (data) {
          // User consent and email
          this.setData(Resources.SessionStorageKeys.IsConsentApproval, true);
          this.setData(Resources.SessionStorageKeys.Email, data.email);

          // Selected address
          const selectedAddress: selectedAddressModel = {
            addressId: data?.addressId,
            addressPostcode: data?.postCode,
            addressText: data?.addressLine,
            gbgVersion: data?.gbgVersion || 'V2',
            dnoId: data?.dnoId || data?.dnoRegion
          };

          if (data?.mpan) {
            selectedAddress.mpan = data?.mpan;
          }

          if (data?.mprn) {
            selectedAddress.mprn = data?.mprn;
          }

          this.setData(Resources.SessionStorageKeys.SelectedAddress, selectedAddress);
          this.setData(Resources.SessionStorageKeys.IsAccessToken, data?.user_loggedin || false);
          this.setItemIntoLocal(Resources.SessionStorageKeys.Postcode, data?.postCode);

          // Normalised correlationId
          if (data?.correlation_id) {
            data['correlationId'] = data?.correlation_id;
            delete data['correlation_id'];
          }

          // EV details
          if (data?.evDetails) {
            this.setData(Resources.SessionStorageKeys.EVDETAILS, data.evDetails);
            this.evService.updateEVStatus(null);
          }

          // Set current usage data
          this.setItemIntoSession(Resources.SessionStorageKeys.CorrelationId, correlationId);
          this.setData(Resources.SessionStorageKeys.DnoId, selectedAddress?.dnoId || data?.dnoRegion || '');
          this.setData(Resources.SessionStorageKeys.CurrentUsage, data);

          // Others important session data
          this.setData(Resources.SessionStorageKeys.Frequency, 'Monthly');
          this.setData(Resources.SessionStorageKeys.IsAddressBlocked, false);

          // Smart meter flags
          this.setItemIntoSession(Resources.SessionStorageKeys.IsNotNow, true);

        } else {
          console.warn('No session data found for the provided correlationId.');
        }

        return data;
      })
    )
  }

  /**
   * @description This method is used to put session data to the server using correlationId. It sends the session data to the server to be stored against the correlationId.
   * @param correlationId string
   * @param data any
   * @returns Observable<any>
   * @throws Error if correlationId is not provided or if the session data cannot be updated
   * @example this.sessionService.putSessionData('12345', { key: 'value' }).subscribe()
   */
  putSessionData(correlationId: string, data: any): Observable<any> {
    if (!correlationId) {
      return of(null);
    }

    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    return this.http.put<any>(`${environment?.api_url}/v1/users/sessionData`, {
      correlationId: correlationId,
      ...data
    }, httpOptions).pipe(
      timeout(30000),
      retry(1),
    );
  }

  // Get Data
  getData(key: string): any {
    if (!!this.storage[key]) {
      try {
        return JSON.parse(this.storage[key]);
      } catch (error) {
        return this.storage[key];
      }
    }
  }

  // Set Data
  setData(key: string, value: any): void {
    if (!!key && !!value) {
      this.storage[key] = (typeof value == 'string') ? value : JSON.stringify(value);
    }
  }

  // Remove Data
  removeData(key: string): void {
    if (!!this.storage[key]) {
      delete this.storage[key];
    }
  }

  // Clear all data
  clearData(): void {
    this.storage = {};
  }
}
